import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ApiClient } from './api-client';
import type {
  ApplicationCard,
  AuthTokens,
  LoginResult,
  WorkspaceContext,
} from './types';

/**
 * Session, tenant and theme in one provider.
 *
 * They belong together because they arrive together: `/api/v1/me/context` returns identity,
 * tenant, permissions and the resolved theme in a single response, so the shell either has a
 * complete picture or none — never a half-configured one.
 */

const STORAGE_KEY = 'clientma.session';

interface StoredSession {
  tokens: AuthTokens;
  context?: WorkspaceContext;
}

export interface SessionState {
  status: 'loading' | 'signed-out' | 'signed-in';
  context: WorkspaceContext | null;
  applications: ApplicationCard[];
  api: ApiClient;

  signIn: (email: string, password: string) => Promise<LoginResult>;
  signOut: () => void;
  reloadContext: () => Promise<void>;

  /** True when the signed-in user holds the permission. Tenant admins hold everything. */
  can: (permission: string) => boolean;

  /** True when the tenant is entitled to the application and the user is assigned to it. */
  hasApplication: (applicationKey: string) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const session = useContext(SessionContext);

  if (!session) {
    throw new Error('useSession must be used inside <SessionProvider>.');
  }

  return session;
}

export interface SessionProviderProps {
  /** Empty means same-origin, which routes through the dev server's proxy. */
  apiBaseUrl: string;
  children: ReactNode;
}

export function SessionProvider({ apiBaseUrl, children }: SessionProviderProps) {
  const [context, setContext] = useState<WorkspaceContext | null>(null);
  const [applications, setApplications] = useState<ApplicationCard[]>([]);
  const [status, setStatus] = useState<SessionState['status']>('loading');

  // A ref rather than state: the API client reads the current tokens on every request and must
  // not be rebuilt when they change, or the in-flight refresh dedupe would be lost.
  const tokensRef = useRef<AuthTokens | null>(null);

  const setTokens = useCallback((next: AuthTokens | null) => {
    tokensRef.current = next;

    if (!next) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const stored = window.localStorage.getItem(STORAGE_KEY);
    const previous: Partial<StoredSession> = stored ? JSON.parse(stored) : {};

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...previous, tokens: next }));
  }, []);

  const signOut = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    tokensRef.current = null;
    setContext(null);
    setApplications([]);
    setStatus('signed-out');
  }, []);

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: apiBaseUrl,
        getTokens: () => tokensRef.current,
        setTokens,
        onSessionExpired: signOut,
      }),
    [apiBaseUrl, setTokens, signOut],
  );

  const reloadContext = useCallback(async () => {
    const workspace = await api.get<WorkspaceContext>('/api/v1/me/context');

    setContext(workspace);
    setStatus('signed-in');

    const stored = window.localStorage.getItem(STORAGE_KEY);
    const previous: Partial<StoredSession> = stored ? JSON.parse(stored) : {};

    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...previous, context: workspace }),
    );

    // The launcher cards are supplementary — the shell renders without them, so a failure
    // here must not take the session down with it.
    try {
      const cards = await api.get<ApplicationCard[]>('/api/v1/me/applications');
      setApplications(cards ?? []);
    } catch {
      setApplications([]);
    }
  }, [api]);

  // Restore a session on load, so a page refresh does not sign the user out.
  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      setStatus('signed-out');
      return;
    }

    let parsed: StoredSession;

    try {
      parsed = JSON.parse(stored) as StoredSession;
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
      setStatus('signed-out');
      return;
    }

    if (!parsed.tokens?.accessToken) {
      setStatus('signed-out');
      return;
    }

    tokensRef.current = parsed.tokens;

    // The cached context renders the shell immediately; the reload then confirms the token is
    // still valid and picks up permission changes made since.
    if (parsed.context) {
      setContext(parsed.context);
      setStatus('signed-in');
    }

    void reloadContext().catch(() => signOut());
    // Deliberately runs once — this is session restoration, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const result = await api.post<LoginResult>(
        '/api/v1/auth/login',
        { email, password },
        { anonymous: true },
      );

      if (result.tokens?.accessToken) {
        setTokens(result.tokens);
        await reloadContext();
      }

      return result;
    },
    [api, reloadContext, setTokens],
  );

  const can = useCallback(
    (permission: string) => {
      if (!context) return false;
      if (context.isTenantAdministrator || context.isTenantOwner) return true;

      return context.permissions.includes(permission);
    },
    [context],
  );

  const hasApplication = useCallback(
    (applicationKey: string) => {
      // Before the cards load, fall back to permissions: a user holding CRM permissions
      // plainly has CRM, and hiding the whole product during a slow launcher fetch would be
      // worse than showing it a moment early.
      if (applications.length === 0) {
        return (
          context?.isTenantAdministrator === true ||
          (context?.permissions ?? []).some((permission) =>
            KNOWN_APPLICATION_PERMISSIONS[applicationKey]?.includes(permission),
          )
        );
      }

      const card = applications.find((entry) => entry.applicationKey === applicationKey);

      return card?.isEntitled === true && card.isAssigned;
    },
    [applications, context],
  );

  // The resolved theme becomes CSS custom properties, so every component reads one source of
  // truth and a tenant switch restyles the app without a re-render.
  useEffect(() => {
    if (!context?.theme) return;

    const root = document.documentElement;
    const theme = context.theme;

    root.style.setProperty('--accent', theme.accentHex);
    root.style.setProperty('--accent-contrast', theme.accentForegroundHex);

    if (theme.brandDisplayName) {
      root.dataset.brand = theme.brandDisplayName;
    }
  }, [context?.theme]);

  const value = useMemo<SessionState>(
    () => ({
      status,
      context,
      applications,
      api,
      signIn,
      signOut,
      reloadContext,
      can,
      hasApplication,
    }),
    [status, context, applications, api, signIn, signOut, reloadContext, can, hasApplication],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * A permission that proves a user has a product, used only while the launcher cards are still
 * loading. Deliberately a read permission: it is the one every user of the product holds.
 */
/**
 * Permissions that plainly imply a product, for the moment before the launcher answers.
 *
 * Every product ClientMa ships needs an entry. Without one, a user of that product sees an empty
 * workspace during the fetch — and, if the fetch fails, keeps seeing it.
 */
/**
 * Enough of a product's permissions to recognise it before the launcher cards arrive.
 *
 * Every product must appear here. Inventory shipped without an entry, so during the launcher
 * fetch its own standalone app could not tell that the person opening it had Inventory —
 * a non-administrator saw "nothing is assigned to you" flash on the product's own address.
 */
const KNOWN_APPLICATION_PERMISSIONS: Record<string, string[]> = {
  crm: ['Account.Read', 'Opportunity.Read', 'Lead.Read'],
  srm: ['Supplier.Read', 'Sourcing.Read', 'PurchaseOrder.Read', 'Contract.Read'],
  ims: ['Stock.Read', 'Item.Read', 'Site.Read'],
};
